import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Plus, Edit, Trash2, Search, HelpCircle, BookOpen, Video, Loader2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import RichTextEditor from '@/components/RichTextEditor';

const PatientContentPage = () => {
  const [tab, setTab] = useState('articles');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // Article modal
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '', slug: '', content: '', excerpt: '', category_id: '',
    featured_image: '', video_url: '', pdf_url: '',
    access_level: 'public', status: 'published', meta_title: '', meta_description: '',
    content_type: 'article',
  });

  // FAQ modal
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({
    question: '', answer: '', category: '', order: 0, status: 'published'
  });

  // Category modal
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', slug: '', description: '', icon: '', order: 0 });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [artRes, catRes, faqRes] = await Promise.all([
        axios.get(`${API}/patient-articles`),
        axios.get(`${API}/patient-categories`),
        axios.get(`${API}/patient-faq`),
      ]);
      setArticles(artRes.data);
      setCategories(catRes.data);
      setFaqs(faqRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const autoSlug = (title) => title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  // ===== ARTICLE CRUD =====
  const saveArticle = async () => {
    const data = { ...articleForm, slug: articleForm.slug || autoSlug(articleForm.title) };
    try {
      if (editingArticle) {
        await axios.put(`${API}/patient-articles/${editingArticle.id}`, data);
      } else {
        await axios.post(`${API}/patient-articles`, data);
      }
      toast.success(editingArticle ? 'Article modifie' : 'Article cree');
      setArticleModalOpen(false);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try { await axios.delete(`${API}/patient-articles/${id}`); toast.success('Supprime'); fetchAll(); }
    catch { toast.error('Erreur'); }
  };

  const openArticleEdit = (a) => {
    setEditingArticle(a);
    setArticleForm({
      title: a.title, slug: a.slug, content: a.content, excerpt: a.excerpt || '',
      category_id: a.category_id, featured_image: a.featured_image || '', video_url: a.video_url || '',
      pdf_url: a.pdf_url || '', access_level: a.access_level, status: a.status,
      meta_title: a.meta_title || '', meta_description: a.meta_description || ''
    });
    setArticleModalOpen(true);
  };

  // ===== FAQ CRUD =====
  const saveFaq = async () => {
    try {
      if (editingFaq) {
        await axios.put(`${API}/patient-faq/${editingFaq.id}`, faqForm);
      } else {
        await axios.post(`${API}/patient-faq`, faqForm);
      }
      toast.success(editingFaq ? 'FAQ modifiée' : 'FAQ créée');
      setFaqModalOpen(false);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Supprimer cette FAQ ?')) return;
    try { await axios.delete(`${API}/patient-faq/${id}`); toast.success('Supprime'); fetchAll(); }
    catch { toast.error('Erreur'); }
  };

  const openFaqEdit = (f) => {
    setEditingFaq(f);
    setFaqForm({ question: f.question, answer: f.answer, category: f.category || '', order: f.order || 0, status: f.status });
    setFaqModalOpen(true);
  };

  // ===== CATEGORY CRUD =====
  const saveCat = async () => {
    const data = { ...catForm, slug: catForm.slug || autoSlug(catForm.name) };
    try {
      if (editingCat) {
        await axios.put(`${API}/patient-categories/${editingCat.id}`, data);
      } else {
        await axios.post(`${API}/patient-categories`, data);
      }
      toast.success(editingCat ? 'Catégorie modifiée' : 'Catégorie créée');
      setCatModalOpen(false);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Supprimer cette categorie ?')) return;
    try { await axios.delete(`${API}/patient-categories/${id}`); toast.success('Supprime'); fetchAll(); }
    catch { toast.error('Erreur'); }
  };

  const filteredArticles = articles.filter(a => {
    const s = a.title.toLowerCase().includes(searchTerm.toLowerCase());
    const c = filterCategory === 'all' || a.category_id === filterCategory;
    return s && c;
  });

  const filteredFaqs = faqs.filter(f =>
    f.question.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'articles', label: 'Articles & Fiches', icon: FileText, count: articles.length },
    { id: 'faq', label: 'FAQ', icon: HelpCircle, count: faqs.length },
    { id: 'categories', label: 'Catégories', icon: BookOpen, count: categories.length },
  ];

  const inputClass = 'w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-2 focus:ring-[#35AFE4]/10 outline-none';
  const labelClass = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-gray-400" size={24} /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 min-h-full" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="patient-content-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 tracking-tight">Espace Patients</h1>
            <p className="text-sm text-gray-400 mt-0.5">Gerez les articles, FAQ et categories pour les patients</p>
          </div>
          <Button
            onClick={() => {
              if (tab === 'articles') { setEditingArticle(null); setArticleForm({ title:'',slug:'',content:'',excerpt:'',category_id:'',featured_image:'',video_url:'',pdf_url:'',access_level:'public',status:'published',meta_title:'',meta_description:'' }); setArticleModalOpen(true); }
              else if (tab === 'faq') { setEditingFaq(null); setFaqForm({ question:'',answer:'',category:'',order:0,status:'published' }); setFaqModalOpen(true); }
              else { setEditingCat(null); setCatForm({ name:'',slug:'',description:'',icon:'',order:0 }); setCatModalOpen(true); }
            }}
            className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white"
            data-testid="btn-new-item"
          >
            <Plus size={16} className="mr-1.5" />
            {tab === 'articles' ? 'Nouvel Article' : tab === 'faq' ? 'Nouvelle FAQ' : 'Nouvelle Catégorie'}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => { setTab(t.id); setSearchTerm(''); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab === t.id ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={16} /> {t.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-[#1e3a5f] text-white' : 'bg-gray-200 text-gray-600'}`}>{t.count}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-5 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder={tab === 'faq' ? 'Rechercher une question...' : 'Rechercher...'}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] outline-none"
            data-testid="search-input"
          />
        </div>

        {/* ===== ARTICLES TAB ===== */}
        {tab === 'articles' && (
          <div className="space-y-3">
            {filterCategory !== 'hide' && (
              <div className="flex gap-2 mb-4 flex-wrap">
                <button onClick={() => setFilterCategory('all')} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filterCategory === 'all' ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Tous</button>
                {categories.map(c => (
                  <button key={c.id} onClick={() => setFilterCategory(c.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filterCategory === c.id ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >{c.name}</button>
                ))}
              </div>
            )}
            {filteredArticles.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <FileText className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-500 font-medium">Aucun article</p>
              </div>
            ) : filteredArticles.map(a => (
              <div key={a.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-sm transition-all" data-testid={`article-${a.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-900">{a.title}</h3>
                      {a.video_url && <Video size={14} className="text-blue-500" />}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${a.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{a.status === 'published' ? 'Publié' : 'Brouillon'}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${a.access_level === 'public' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{a.access_level === 'public' ? 'Public' : 'Membres'}</span>
                    </div>
                    <p className="text-xs text-gray-400">{a.category_name || 'Sans categorie'} &middot; {a.views_count || 0} vues</p>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => openArticleEdit(a)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1e3a5f]" data-testid={`edit-article-${a.id}`}><Edit size={16} /></button>
                    <button onClick={() => deleteArticle(a.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={`delete-article-${a.id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== FAQ TAB ===== */}
        {tab === 'faq' && (
          <div className="space-y-3">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <HelpCircle className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-500 font-medium">Aucune FAQ</p>
              </div>
            ) : filteredFaqs.map(f => (
              <div key={f.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-sm transition-all" data-testid={`faq-${f.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <GripVertical size={14} className="text-gray-300" />
                      <h3 className="font-bold text-gray-900">{f.question}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${f.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{f.status === 'published' ? 'Publié' : 'Brouillon'}</span>
                    </div>
                    <p className="text-sm text-gray-500 ml-6 line-clamp-2">{f.answer}</p>
                    {f.category && <p className="text-[10px] text-gray-400 ml-6 mt-1">Catégorie: {f.category}</p>}
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => openFaqEdit(f)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1e3a5f]"><Edit size={16} /></button>
                    <button onClick={() => deleteFaq(f.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== CATEGORIES TAB ===== */}
        {tab === 'categories' && (
          <div className="space-y-3">
            {categories.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <BookOpen className="mx-auto text-gray-300 mb-3" size={48} />
                <p className="text-gray-500 font-medium">Aucune categorie</p>
              </div>
            ) : categories.map(c => (
              <div key={c.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-sm transition-all flex items-center justify-between" data-testid={`cat-${c.id}`}>
                <div>
                  <h3 className="font-bold text-gray-900">{c.name}</h3>
                  <p className="text-xs text-gray-400">/{c.slug} &middot; Ordre: {c.order}</p>
                  {c.description && <p className="text-sm text-gray-500 mt-1">{c.description}</p>}
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => { setEditingCat(c); setCatForm({ name:c.name, slug:c.slug, description:c.description||'', icon:c.icon||'', order:c.order }); setCatModalOpen(true); }} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1e3a5f]"><Edit size={16} /></button>
                  <button onClick={() => deleteCat(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== ARTICLE MODAL ===== */}
      <Dialog open={articleModalOpen} onOpenChange={setArticleModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArticle ? 'Modifier l\'article' : 'Nouvel article patient'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Type de contenu */}
            <div>
              <label className={labelClass}>Type de contenu</label>
              <div className="flex gap-2">
                {[
                  { value: 'article', label: 'Fiche / Article', icon: '📄' },
                  { value: 'video', label: 'Tuto vidéo', icon: '🎬' },
                  { value: 'faq', label: 'FAQ', icon: '❓' },
                ].map(t => (
                  <button key={t.value} type="button"
                    onClick={() => setArticleForm(p => ({ ...p, content_type: t.value }))}
                    className={`flex-1 py-2.5 px-3 rounded-xl border text-sm font-semibold transition-all ${articleForm.content_type === t.value ? 'border-[#35AFE4] bg-[#eaf6fc] text-[#1e3a5f]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    data-testid={`content-type-${t.value}`}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>Titre</label><input value={articleForm.title} onChange={e => setArticleForm({...articleForm, title: e.target.value})} className={inputClass} data-testid="article-title" /></div>
              <div><label className={labelClass}>Slug</label><input value={articleForm.slug} onChange={e => setArticleForm({...articleForm, slug: e.target.value})} placeholder="auto-généré" className={inputClass} /></div>
            </div>
            <div><label className={labelClass}>Catégorie</label>
              <select value={articleForm.category_id} onChange={e => setArticleForm({...articleForm, category_id: e.target.value})} className={inputClass} data-testid="article-category">
                <option value="">Choisir une catégorie</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Video URL — prominent for video type */}
            {articleForm.content_type === 'video' && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <label className={labelClass}>URL vidéo YouTube *</label>
                <input value={articleForm.video_url} onChange={e => setArticleForm({...articleForm, video_url: e.target.value})} className={inputClass}
                  placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..." data-testid="article-video-url" />
                {articleForm.video_url && articleForm.video_url.includes('youtu') && (
                  <div className="mt-3 rounded-lg overflow-hidden">
                    <iframe width="100%" height="200" src={`https://www.youtube.com/embed/${articleForm.video_url.match(/(?:v=|youtu\.be\/)([^&]+)/)?.[1] || ''}`}
                      frameBorder="0" allowFullScreen title="preview" />
                  </div>
                )}
              </div>
            )}

            <div><label className={labelClass}>Contenu</label><RichTextEditor value={articleForm.content} onChange={val => setArticleForm({...articleForm, content: val})} height="200px" /></div>
            <div><label className={labelClass}>Résumé</label><Textarea value={articleForm.excerpt} onChange={e => setArticleForm({...articleForm, excerpt: e.target.value})} rows={2} /></div>

            {articleForm.content_type !== 'video' && (
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelClass}>Image</label><input value={articleForm.featured_image} onChange={e => setArticleForm({...articleForm, featured_image: e.target.value})} className={inputClass} placeholder="URL de l'image" /></div>
                <div><label className={labelClass}>Vidéo URL</label><input value={articleForm.video_url} onChange={e => setArticleForm({...articleForm, video_url: e.target.value})} className={inputClass} placeholder="YouTube embed (optionnel)" /></div>
                <div><label className={labelClass}>PDF URL</label><input value={articleForm.pdf_url} onChange={e => setArticleForm({...articleForm, pdf_url: e.target.value})} className={inputClass} /></div>
              </div>
            )}
            {articleForm.content_type === 'video' && (
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelClass}>Image miniature</label><input value={articleForm.featured_image} onChange={e => setArticleForm({...articleForm, featured_image: e.target.value})} className={inputClass} placeholder="URL de la miniature" /></div>
                <div><label className={labelClass}>PDF complémentaire</label><input value={articleForm.pdf_url} onChange={e => setArticleForm({...articleForm, pdf_url: e.target.value})} className={inputClass} /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>Acces</label>
                <select value={articleForm.access_level} onChange={e => setArticleForm({...articleForm, access_level: e.target.value})} className={inputClass}>
                  <option value="public">Public</option>
                  <option value="members_only">Membres uniquement</option>
                </select>
              </div>
              <div><label className={labelClass}>Statut</label>
                <select value={articleForm.status} onChange={e => setArticleForm({...articleForm, status: e.target.value})} className={inputClass}>
                  <option value="published">Publie</option>
                  <option value="draft">Brouillon</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>Meta Titre (SEO)</label><input value={articleForm.meta_title} onChange={e => setArticleForm({...articleForm, meta_title: e.target.value})} className={inputClass} /></div>
              <div><label className={labelClass}>Meta Description (SEO)</label><input value={articleForm.meta_description} onChange={e => setArticleForm({...articleForm, meta_description: e.target.value})} className={inputClass} /></div>
            </div>
            <Button onClick={saveArticle} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-article">
              {editingArticle ? 'Sauvegarder' : 'Créer l\'article'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== FAQ MODAL ===== */}
      <Dialog open={faqModalOpen} onOpenChange={setFaqModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'Modifier la FAQ' : 'Nouvelle FAQ'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><label className={labelClass}>Question</label><input value={faqForm.question} onChange={e => setFaqForm({...faqForm, question: e.target.value})} className={inputClass} data-testid="faq-question" /></div>
            <div><label className={labelClass}>Réponse</label><RichTextEditor value={faqForm.answer} onChange={val => setFaqForm({...faqForm, answer: val})} height="150px" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>Catégorie</label><input value={faqForm.category} onChange={e => setFaqForm({...faqForm, category: e.target.value})} className={inputClass} placeholder="Ex: Lentilles, Entretien..." /></div>
              <div><label className={labelClass}>Ordre</label><input type="number" value={faqForm.order} onChange={e => setFaqForm({...faqForm, order: parseInt(e.target.value) || 0})} className={inputClass} /></div>
            </div>
            <div><label className={labelClass}>Statut</label>
              <select value={faqForm.status} onChange={e => setFaqForm({...faqForm, status: e.target.value})} className={inputClass}>
                <option value="published">Publie</option>
                <option value="draft">Brouillon</option>
              </select>
            </div>
            <Button onClick={saveFaq} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-faq">
              {editingFaq ? 'Sauvegarder' : 'Créer la FAQ'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CATEGORY MODAL ===== */}
      <Dialog open={catModalOpen} onOpenChange={setCatModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCat ? 'Modifier la categorie' : 'Nouvelle categorie'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><label className={labelClass}>Nom</label><input value={catForm.name} onChange={e => setCatForm({...catForm, name: e.target.value})} className={inputClass} data-testid="cat-name" /></div>
            <div><label className={labelClass}>Slug</label><input value={catForm.slug} onChange={e => setCatForm({...catForm, slug: e.target.value})} placeholder="auto-généré" className={inputClass} /></div>
            <div><label className={labelClass}>Description</label><RichTextEditor value={catForm.description} onChange={val => setCatForm({...catForm, description: val})} height="100px" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>Icone</label><input value={catForm.icon} onChange={e => setCatForm({...catForm, icon: e.target.value})} className={inputClass} placeholder="fa-eye" /></div>
              <div><label className={labelClass}>Ordre</label><input type="number" value={catForm.order} onChange={e => setCatForm({...catForm, order: parseInt(e.target.value) || 0})} className={inputClass} /></div>
            </div>
            <Button onClick={saveCat} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="save-cat">
              {editingCat ? 'Sauvegarder' : 'Créer la categorie'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default PatientContentPage;
